import React, { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, FileText, Send, Loader2, CheckCircle2, ArrowRight } from "lucide-react";

/**
 * Animated 60-second demo: types the prompt, then reveals an AI-generated quote
 * with progressive line appearances, ending with a PDF/send action. Loops.
 */
export default function InteractiveDemo() {
  const [phase, setPhase] = useState("typing"); // typing → thinking → reveal → done → reset
  const [typed, setTyped] = useState("");
  const [itemsShown, setItemsShown] = useState(0);
  const [highlightCta, setHighlightCta] = useState(false);
  const timerRef = useRef(null);

  const PROMPT = "Pintar piso 80 m², 2 manos, color claro, urgencia normal";
  const ITEMS = useMemo(() => ([
    { c: "Preparación y protección de superficies", q: 80, u: "m²", p: 2.5 },
    { c: "Lijado y tapado de imperfecciones", q: 80, u: "m²", p: 1.8 },
    { c: "Aplicación pintura plástica blanca (2 manos)", q: 80, u: "m²", p: 4.2 },
    { c: "Limpieza final y retirada de materiales", q: 1, u: "servicio", p: 40 },
  ]), []);
  const subtotal = ITEMS.reduce((s, it) => s + it.q * it.p, 0);
  const iva = subtotal * 0.21;
  const total = subtotal + iva;

  useEffect(() => {
    clearTimeout(timerRef.current);

    if (phase === "typing") {
      if (typed.length < PROMPT.length) {
        timerRef.current = setTimeout(() => setTyped(PROMPT.slice(0, typed.length + 1)), 42);
      } else {
        timerRef.current = setTimeout(() => setPhase("thinking"), 520);
      }
      return;
    }

    if (phase === "thinking") {
      timerRef.current = setTimeout(() => setPhase("reveal"), 1700);
      return;
    }

    if (phase === "reveal") {
      if (itemsShown < ITEMS.length) {
        timerRef.current = setTimeout(() => setItemsShown(itemsShown + 1), 380);
      } else {
        const t1 = setTimeout(() => setHighlightCta(true), 500);
        const t2 = setTimeout(() => setPhase("done"), 1700);
        timerRef.current = t2;
        return () => { clearTimeout(t1); clearTimeout(t2); };
      }
      return;
    }

    if (phase === "done") {
      timerRef.current = setTimeout(() => {
        setTyped("");
        setItemsShown(0);
        setHighlightCta(false);
        setPhase("typing");
      }, 3600);
    }

    return () => clearTimeout(timerRef.current);
  }, [phase, typed, itemsShown, ITEMS.length]);

  const showCaret = phase === "typing";
  const thinking = phase === "thinking";
  const revealing = phase === "reveal" || phase === "done";

  return (
    <div data-testid="interactive-demo" className="relative">
      <div className="absolute -top-3 left-4 z-10 px-2 py-1 bg-orange-600 text-white text-[10px] font-black tracking-widest uppercase">
        ⏱ Demo en vivo — 9 seg
      </div>
      <div className="bg-white border-2 border-slate-900 shadow-[8px_8px_0_#0f172a]">
        {/* Browser chrome */}
        <div className="bg-slate-900 text-slate-400 px-4 h-9 flex items-center gap-2 text-[10px] font-mono">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
          </div>
          <span className="ml-3">autopresupuestos.pro / generator</span>
        </div>

        <div className="p-5 space-y-4">
          {/* Prompt input */}
          <div>
            <div className="kbar mb-2">Describe el trabajo</div>
            <div className="relative bg-slate-50 border border-slate-300 px-3 h-12 flex items-center text-sm">
              <span className="text-slate-900">{typed}</span>
              {showCaret && <span className="w-[2px] h-5 bg-orange-600 ml-0.5 animate-pulse-soft" />}
            </div>
          </div>

          {/* CTA button */}
          <button
            disabled
            className={`w-full h-11 inline-flex items-center justify-center gap-2 font-bold text-sm transition-all ${
              highlightCta ? "bg-green-600 text-white" : "bg-orange-600 text-white"
            }`}
          >
            {thinking && <><Loader2 className="w-4 h-4 animate-spin" /> Analizando con IA…</>}
            {phase === "typing" && <><Sparkles className="w-4 h-4" /> Generar con IA</>}
            {revealing && !highlightCta && <><Sparkles className="w-4 h-4" /> Generando…</>}
            {highlightCta && <><CheckCircle2 className="w-4 h-4" /> Listo en 9 segundos</>}
          </button>

          {/* Result */}
          <div className="min-h-[260px] border-t border-slate-200 pt-4">
            {revealing ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="kbar text-slate-400">Presupuesto generado</div>
                    <div className="font-display font-extrabold text-lg leading-tight">Pintura piso 80 m² · 2 manos</div>
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">F-2026-DEMO</div>
                </div>

                <div className="space-y-1.5 text-sm">
                  {ITEMS.slice(0, itemsShown).map((it, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between py-1.5 border-b border-slate-100 animate-fade-in-up"
                    >
                      <span className="text-slate-700 flex-1 pr-2">{it.c}</span>
                      <span className="text-xs text-slate-400 tabular-nums">{it.q} {it.u}</span>
                      <span className="ml-3 font-mono tabular-nums text-sm font-semibold w-16 text-right">
                        {(it.q * it.p).toFixed(2)} €
                      </span>
                    </div>
                  ))}
                </div>

                {itemsShown >= ITEMS.length && (
                  <div className="mt-3 pt-3 border-t border-slate-900 flex items-center justify-between animate-fade-in-up">
                    <div className="flex gap-2">
                      <span className="inline-flex items-center gap-1 h-8 px-2.5 border border-slate-300 text-xs font-bold bg-white">
                        <FileText className="w-3 h-3" /> PDF
                      </span>
                      <span className="inline-flex items-center gap-1 h-8 px-2.5 border border-slate-300 text-xs font-bold bg-white">
                        <Send className="w-3 h-3" /> Enviar
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] text-slate-400 uppercase tracking-widest">Total con IVA</div>
                      <div className="font-display font-extrabold text-2xl tabular-nums">{total.toFixed(2)} €</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-center py-8">
                {thinking ? (
                  <div>
                    <Loader2 className="w-8 h-8 text-orange-600 animate-spin mx-auto" />
                    <div className="mt-3 text-sm font-semibold text-slate-700">IA analizando tipo de obra…</div>
                    <div className="text-xs text-slate-400 mt-1">Calculando materiales, mano de obra e IVA</div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-400">Escribe el trabajo y pulsa generar</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
